import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Sparkles, LogOut, ChevronRight, PenTool, Users, BookOpen, CreditCard } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { ThemeSwitch } from './theme-switch';
import { LangSelect } from './lang-select';

// ---------------------------------------------------------------------------
// LandingNav — shared fixed navbar for landing and pricing pages
// ---------------------------------------------------------------------------
export function LandingNav() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  const userInitials =
    user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToApp = () => {
    navigate('/app');
  };

  const handleLogin = () => {
    navigate('/app?auth=login');
  };

  const handleSignup = () => {
    navigate('/app?auth=signup');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <a
              href="/"
              className="flex items-center gap-2 font-serif text-lg sm:text-2xl font-bold tracking-tight text-primary"
            >
              <img src="/log-black.png" alt="" aria-hidden="true" className="h-7 w-7 sm:h-8 sm:w-8 object-contain dark:hidden" />
              <img src="/log-white.png" alt="" aria-hidden="true" className="hidden h-7 w-7 sm:h-8 sm:w-8 object-contain dark:block" />
              <span>reformu<span className="text-accent">.</span>lat</span>
            </a>
          </div>

          <div className="hidden md:flex items-center gap-5">
            {/* Group 1 — navigation links */}
            <div className="flex items-center gap-6">
              <a
                href="/#authors"
                className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('landingNav.forAuthors', 'Para Autores')}
              </a>
              <a
                href="/#professionals"
                className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('landingNav.forProfessionals', 'Para Profissionais')}
              </a>
              <a
                href="/#ai-features"
                className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors flex items-center gap-1"
              >
                <Sparkles size={14} className="text-accent" />
                {t('landingNav.aiFeatures', 'Recursos de IA')}
              </a>
              <a
                href="/#services"
                className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('landingNav.services', 'Serviços')}
              </a>
              <a
                href="/#pricing"
                className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('landingNav.pricing', 'Preços')}
              </a>
            </div>

            <span className="h-5 w-px bg-border" aria-hidden="true" />

            {/* Group 2 — general functions (theme + language) */}
            <div className="flex items-center gap-2">
              <ThemeSwitch sz={28} />
              <LangSelect />
            </div>

            <span className="h-5 w-px bg-border" aria-hidden="true" />

            {/* Group 3 — auth */}
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={goToApp}
                  className="text-sm font-medium text-foreground hover:text-foreground/70 transition-colors"
                >
                  {t('landingNav.goToApp', 'Acessar App')}
                </button>
                <button
                  onClick={goToApp}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-accent text-accent-foreground text-sm font-bold shadow-lg hover:scale-105 transition-transform"
                  title={user?.name || t('landingNav.userFallback', 'Usuário')}
                >
                  {userInitials}
                </button>
                <button
                  onClick={logout}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                  title={t('landingNav.logout', 'Sair')}
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLogin}
                  className="text-sm font-medium text-foreground hover:text-foreground/70 transition-colors"
                >
                  {t('landingNav.login', 'Entrar')}
                </button>
                <button
                  onClick={handleSignup}
                  className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground transition-colors hover:opacity-90"
                >
                  {t('landingNav.signup', 'Cadastrar')}
                </button>
              </div>
            )}
          </div>

          <div className="md:hidden flex items-center space-x-1">
            <ThemeSwitch sz={24} />
            <LangSelect />
            {isAuthenticated && (
              <button
                onClick={goToApp}
                className="w-9 h-9 rounded-full flex items-center justify-center bg-accent text-accent-foreground text-xs font-bold"
              >
                {userInitials}
              </button>
            )}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-foreground">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm md:hidden"
            />
            <motion.aside
              key="panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
              className="fixed top-0 right-0 bottom-0 z-[70] flex w-[82%] max-w-xs flex-col bg-card border-l border-border shadow-2xl md:hidden"
            >
              {/* Drawer header */}
              <div className="flex h-16 items-center justify-between border-b border-border px-5">
                <span className="flex items-center gap-2 font-serif text-xl font-bold tracking-tight text-primary">
                  <img src="/log-black.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain dark:hidden" />
                  <img src="/log-white.png" alt="" aria-hidden="true" className="hidden h-7 w-7 object-contain dark:block" />
                  <span>reformu<span className="text-accent">.</span>lat</span>
                </span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={t('landingNav.closeMenu', 'Fechar menu')}
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Links */}
              <nav className="flex-1 overflow-y-auto px-3 py-4">
                {[
                  { label: 'For Authors', href: '/#authors', icon: PenTool },
                  { label: 'For Professionals', href: '/#professionals', icon: Users },
                  { label: 'AI Features', href: '/#ai-features', icon: Sparkles },
                  { label: 'Services', href: '/#services', icon: BookOpen },
                  { label: 'Pricing', href: '/pricing', icon: CreditCard },
                ].map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between gap-3 rounded-xl px-3 py-3 text-base font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={18} className="text-accent" />
                      {label}
                    </span>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </a>
                ))}
              </nav>

              {/* Footer actions */}
              <div className="space-y-3 border-t border-border p-4">
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={() => { goToApp(); setMobileMenuOpen(false); }}
                      className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      Go to App
                    </button>
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                    >
                      <LogOut size={16} /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => { handleLogin(); setMobileMenuOpen(false); }}
                      className="w-full rounded-full border border-border py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      Log in
                    </button>
                    <button
                      onClick={() => { handleSignup(); setMobileMenuOpen(false); }}
                      className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      Sign up
                    </button>
                  </>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
